var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './BasicStatus.js';
import { StatusPickerPopover } from '../popover/StatusPickerPopover.js';
import '../popover/StatusPickerPopover.js';
import '../icon/Icon.js';
const VARIANT_DOT_COLOR = {
    primary: '#0078d4',
    secondary: '#6b7280',
    success: '#28a745',
    warning: '#d4a017',
    danger: '#dc3545',
};
function resolveDotColor(opt) {
    if (opt.dotColor)
        return opt.dotColor;
    if (!opt.variant)
        return undefined;
    return VARIANT_DOT_COLOR[opt.variant] ?? opt.variant;
}
/**
 * Editable status pill — renders a `<basic-status>` wrapped in a button.
 * Clicking opens a `<status-picker-popover>` listing the other options;
 * selecting a different value dispatches `status-change` `{ value }` and the
 * popover closes. Locking the current option (or setting `disabled`) makes
 * the pill read-only and displays an optional lock icon next to the label.
 *
 * Events:
 *  - `status-change` `{ value: string }` — emitted when the user picks a
 *    different value. The host is responsible for updating `value`.
 */
let BasicEditableStatus = class BasicEditableStatus extends LitElement {
    constructor() {
        super(...arguments);
        this.value = '';
        this.options = [];
        this.size = 'small';
        this.dot = false;
        this.solid = false;
        this.special = false;
        this.disabled = false;
        this._onClick = (e) => {
            if (this._isLocked)
                return;
            const anchor = e.currentTarget;
            const picker = new StatusPickerPopover();
            picker.options = this.options.map((opt) => {
                const dotColor = resolveDotColor(opt);
                const out = { value: opt.value, label: opt.label };
                if (dotColor != null)
                    out.dotColor = dotColor;
                if (opt.locked != null)
                    out.locked = opt.locked;
                return out;
            });
            picker.value = this.value;
            picker.addEventListener('status-change', (ev) => {
                const next = ev.detail.value;
                if (next === this.value)
                    return;
                this.dispatchEvent(new CustomEvent('status-change', {
                    detail: { value: next },
                    bubbles: true,
                    composed: true,
                }));
            });
            picker.openAt(anchor);
        };
    }
    get _currentOption() {
        return this.options.find((o) => o.value === this.value);
    }
    get _isLocked() {
        return this.disabled || !!this._currentOption?.locked;
    }
    render() {
        const current = this._currentOption;
        const label = current?.label ?? this.value;
        const variant = current?.variant ?? 'primary';
        const locked = this._isLocked;
        const title = locked ? this.lockedTooltip : this.changeTooltip;
        return html `
      <button
        type="button"
        class="button"
        ?disabled=${locked}
        title=${title ?? nothing}
        @click=${this._onClick}
      >
        <basic-status
          .variant=${variant}
          .status=${label}
          .size=${this.size}
          ?dot=${this.dot}
          ?solid=${this.solid}
          ?special=${this.special}
        >
          ${current?.locked
            ? html `<span class="lock"
                ><basic-icon name="lock" size="11"></basic-icon
              ></span>`
            : nothing}
        </basic-status>
      </button>
    `;
    }
};
BasicEditableStatus.styles = css `
    :host {
      display: inline-block;
    }
    .button {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font: inherit;
      color: inherit;
      transition: filter 0.15s;
    }
    .button:hover:not([disabled]) {
      filter: brightness(0.97);
    }
    .button[disabled] {
      cursor: default;
      opacity: 0.95;
    }
    .lock {
      display: inline-flex;
      align-items: center;
      margin-left: 4px;
      vertical-align: -1px;
      opacity: 0.85;
    }
  `;
__decorate([
    property({ type: String })
], BasicEditableStatus.prototype, "value", void 0);
__decorate([
    property({ type: Array })
], BasicEditableStatus.prototype, "options", void 0);
__decorate([
    property({ type: String, reflect: true })
], BasicEditableStatus.prototype, "size", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicEditableStatus.prototype, "dot", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicEditableStatus.prototype, "solid", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicEditableStatus.prototype, "special", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BasicEditableStatus.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'change-tooltip' })
], BasicEditableStatus.prototype, "changeTooltip", void 0);
__decorate([
    property({ type: String, attribute: 'locked-tooltip' })
], BasicEditableStatus.prototype, "lockedTooltip", void 0);
BasicEditableStatus = __decorate([
    customElement('basic-editable-status')
], BasicEditableStatus);
export { BasicEditableStatus };
//# sourceMappingURL=BasicEditableStatus.js.map